import { useMemo } from "react";
import { Globe2 } from "lucide-react";

interface CountryEntry {
  name: string;
  code?: string;
  value: number;
}

interface WorldMapProps {
  entries: CountryEntry[];
}

// Best-effort: country code "US" -> 🇺🇸. Falls back gracefully on Windows
// (which renders code points as raw letters), so we also always show the code chip.
const codeToFlag = (code?: string): string => {
  if (!code || code.length !== 2 || !/^[A-Za-z]{2}$/.test(code)) return "";
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split("")
      .map((c) => 127397 + c.charCodeAt(0)),
  );
};

const WorldMap = ({ entries }: WorldMapProps) => {
  const sorted = useMemo(
    () => [...entries].sort((a, b) => b.value - a.value),
    [entries],
  );

  const total = useMemo(
    () => sorted.reduce((s, e) => s + e.value, 0),
    [sorted],
  );

  const max = sorted[0]?.value || 1;

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-ink-400">
        <Globe2 size={28} className="mb-2" />
        <p className="text-sm">No geo data yet.</p>
      </div>
    );
  }

  // Split into two columns when more than 6 entries
  const half = Math.ceil(sorted.length / 2);
  const left = sorted.slice(0, half);
  const right = sorted.slice(half);

  const Row = ({ entry, rank }: { entry: CountryEntry; rank: number }) => {
    const pct = total > 0 ? (entry.value / total) * 100 : 0;
    const intensity = Math.max(0.25, entry.value / max);
    const flag = codeToFlag(entry.code);

    return (
      <div className="group flex items-center gap-3 py-2.5">
        <div className="shrink-0 w-7 h-7 flex items-center justify-center text-[10px] font-display font-bold text-ink-400 tabular-nums">
          {String(rank).padStart(2, "0")}
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {flag && (
            <span className="text-lg leading-none select-none" aria-hidden>
              {flag}
            </span>
          )}
          {entry.code && (
            <span className="text-[10px] font-mono font-semibold uppercase text-ink-600 bg-ink-100 px-1.5 py-0.5 rounded">
              {entry.code}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-sm font-medium text-ink-900 truncate">
              {entry.name}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] text-ink-400 tabular-nums">
                {pct.toFixed(1)}%
              </span>
              <span className="text-sm font-semibold text-ink-900 tabular-nums w-8 text-right">
                {entry.value}
              </span>
            </div>
          </div>
          <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-gradient transition-all duration-700"
              style={{
                width: `${(entry.value / max) * 100}%`,
                opacity: 0.35 + 0.65 * intensity,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
      <div className="divide-y divide-ink-100/70">
        {left.map((e, i) => (
          <Row key={e.name} entry={e} rank={i + 1} />
        ))}
      </div>
      <div className="divide-y divide-ink-100/70">
        {right.map((e, i) => (
          <Row key={e.name} entry={e} rank={half + i + 1} />
        ))}
      </div>
    </div>
  );
};

export default WorldMap;
